<script setup lang="ts">
import { storeToRefs } from "pinia";
import { ref, computed, watchEffect, onMounted } from "vue";

import "@maersk-global/mds-components-core-tag";
import "@maersk-global/mds-components-core-button";
import "@maersk-global/mds-components-core-notification";
import "@maersk-global/mds-components-core-loading-indicator";

import { useImportSearchIdsStore } from "@/store/import/import_id_store";
import { useSaveContainer } from "@/store/import/save_container_store";
import { useSearchIdsStore } from "@/store/common/search_id_store";
import { useImportLocationStore } from "@/store/import/import_location_store";
import { useImportAssetStore } from "@/store/import/import_asset_store";
import { useTerminalConfigurationStore } from "@/store/common/terminal_configuration_store";
import { useGlobalStore } from "@/store/global";

import { InputTypeCode } from "@/enum/search_enum";
import { regularExpressions } from "@/utils/regular_expression";
import { clearLocalStorageData, getDataFromLS, isAuthenticatedUser } from "@/utils/common";
import Loader from "@/components/Loader.vue";
import type { Terminal } from "@/interface/common/location_inteface";
import SearchResultErrorView from "@/components/common/SearchResultErrorView.vue";
import { appButtonClick } from "@/utils/analytics";

const globalStore = useGlobalStore();
const searchStore = useImportSearchIdsStore();
const saveContainerStore = useSaveContainer();
const commonSearchStore = useSearchIdsStore();
const locationStore = useImportLocationStore();
const terminalConfigurationStore = useTerminalConfigurationStore();
const importAssets = useImportAssetStore();
const backSpaceCount = ref<number>(0);
const isAddInProgress = ref<boolean>(false);
const inputTextValue = ref<string>("");
const { getImportSearchIds } = storeToRefs(searchStore);
const searchIdLimit = computed((): number => terminalConfigurationStore.searchIdLimit);
const containerAddedJouneryCompleted = ref<boolean>(false);
const regions = computed((): any => locationStore.filteredLocations);

onMounted(async () => {
  const isUserAuthenticated = isAuthenticatedUser();
  const searchedContainers = getDataFromLS("containerNumbers");
  if (!containerAddedJouneryCompleted.value && searchedContainers && searchedContainers.length && isUserAuthenticated) {
    isAddInProgress.value = true;
    commonSearchStore.setScreenLoadingBack(true);
    inputTextValue.value = searchedContainers.join(",");
    onInputChange(
      {
        code: "Enter",
        data: inputTextValue.value,
        target: {
          value: inputTextValue.value
        }
      },
      true
    );
    const terminalValue = getDataFromLS("saveContainerTerminal");
    commonSearchStore.setTransactionType("PUI");
    if (terminalValue) {
      locationStore.setTerminal(terminalValue);
      locationStore.setTempLocation(terminalValue);
    }
    const addToWatchListPayload = {
      terminalId: terminalValue.id,
      containers: [],
      validate: false
    };
    const selectedContainersValue = getDataFromLS("selectedContainers");
    selectedContainersValue.forEach((element: any) => {
      addToWatchListPayload.containers.push({
        containerId: element.containerNumber,
        billOfLading: element.billOfLadingNumber
      });
    });

    await saveContainerStore.saveContainer(addToWatchListPayload);
    await terminalConfigurationStore.fetchConfiguration(terminalValue.id.toLocaleLowerCase());
    await importAssets.fetchImportAssets();
    clearLocalStorageData();
    commonSearchStore.setScreenLoadingBack(false);
    isAddInProgress.value = false;
    containerAddedJouneryCompleted.value = true;
  }
});

const getTerminalDetailsById = (regions, terminalId) => {
  for (const region of regions) {
    for (const country of region.countries) {
      for (const terminal of country.terminals) {
        if (terminal.id.toLocaleLowerCase() === terminalId) {
          const selectedAnalyticsTerminalDetails = {
            region: region.name,
            country: country.name,
            terminalName: terminal.name,
            oneStreamCode: terminal.oneStreamCode,
            tabName: "Import Availability"
          };
          locationStore.updateAnayticsTerminalDetails(selectedAnalyticsTerminalDetails);
          return selectedAnalyticsTerminalDetails;
        }
      }
    }
  }
  return null; // Not found
};

const labels = {
  containerSearchHeading: globalStore.sitecoreData.headings.search_heading_import,
  searchId: globalStore.sitecoreData.headings.search_ids,
  invalidId: globalStore.sitecoreData.search.invalid_id,
  remainingEntries: globalStore.sitecoreData.search.entries_remaining,
  clear: globalStore.sitecoreData.common.clear,
  search: globalStore.sitecoreData.common.search,
  isAddInProgress: globalStore.sitecoreData.common.add_in_progress
};

const onInputChange = (event: any, addDirectly = false): void => {
  // event is of type InputEvent
  const inputValue = event.target.value;
  if (regularExpressions.separatorRegex.test(event.data)) {
    const list: string[] = inputValue.split(regularExpressions.separatorRegex);
    list.forEach((element: string): void => {
      searchStore.addImportSearchId(element);
    });
    inputTextValue.value = "";
  } else if (event.inputType === InputTypeCode.PASTE) {
    const list = inputValue?.split(regularExpressions.separatorRegex);
    list.forEach((element: string): void => {
      searchStore.addImportSearchId(element);
    });
    inputTextValue.value = "";
  } else {
    inputTextValue.value = inputValue;
    if (addDirectly) {
      searchStore.addImportSearchId(inputValue);
    }
  }
};

// Coding

const getInputPlaceHolder = computed((): string => {
  return labels.searchId;
});

const keyPressHandler = (event: any): void => {
  // event is of type InputEvent
  if (event.code === InputTypeCode.ENTER) {
    onEnterHandler(event);
  }
  if (event.target.value === "" && event.code === InputTypeCode.BACKSPACE) {
    backSpaceCount.value++;
    if (backSpaceCount.value > 1) {
      getImportSearchIds.value.pop();
    }
  } else {
    backSpaceCount.value = 0;
  }
};

const onBlurHandler = (event: InputEvent) => {
  onEnterHandler(event);
};

const onEnterHandler = (event: any): void => {
  // event is of type InputEvent
  const inputValue = event.target.value.trim().toString();
  if (inputValue === "") return;
  searchStore.addImportSearchId(inputValue);
  inputTextValue.value = "";
};

const removeIds = (id: string): void => {
  searchStore.removeSearchId(id);
};

// Fetch the selected terminal id
const tempSelectedTerminal = computed((): Terminal => locationStore.tempLocation);
// noResultIds
const noResultIds = computed((): string[] => importAssets.noResultIds);
// Checking if there was any error
const errorCode = computed((): string => importAssets.error);

watchEffect((): void => {
  if (noResultIds.value.length > 0) {
    console.log(noResultIds.value.length);
  }
});

watchEffect((): void => {
  if (getImportSearchIds.value.length > 0) {
    console.log(getImportSearchIds.value.length);
  }
});

const validateIds = (): void => {
  locationStore.setTerminal(locationStore.tempLocation);
  terminalConfigurationStore.fetchConfiguration(locationStore.tempLocation.id.toLocaleLowerCase());
  saveContainerStore.clearAllStates();
  importAssets.clearInvalidIds();
  const selectedTerminal = getTerminalDetailsById(regions.value, locationStore.tempLocation.id.toLocaleLowerCase());
  appButtonClick("Import Availability", "Search", selectedTerminal.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails,
    searchedAssetIds: searchStore.getImportSearchIds
  });
  importAssets.fetchImportAssets();
};

// Clear the search ids
const clearSearchIds = (): void => {
  saveContainerStore.clearAllStates();
  searchStore.clearSearchIds();
  importAssets.resetStore();
};

const resetError = (): void => {
  importAssets.resetError();
};
const getTagAppearance = (id: string) => {
  return noResultIds.value.includes(id) ? "error" : "neutral";
};
</script>

<!-- UI elements -->
<template>
  <div v-if="isAddInProgress">
    <Loader :isLoading="isAddInProgress" :loadingMessage="labels.isAddInProgress" />
  </div>
  <div>
    <div class="import_availability_heading">
      {{ labels.containerSearchHeading }}
    </div>
    <div class="search-view__input-container">
      <div class="search-view__grid-container" data-test="search-view__grid-container">
        <mc-tag
          v-for="(id, index) in getImportSearchIds"
          :key="index"
          :label.prop="id"
          withaction="true"
          :appearance="getTagAppearance(id)"
          fit="medium"
          icon="container"
          class="search-view__tag"
          @dismiss="removeIds(id)"
        >
        </mc-tag>
        <mc-input
          v-model="inputTextValue"
          data-test="search-input"
          :placeholder="getInputPlaceHolder"
          hiddenlabel
          name="search"
          type="text"
          style="flex: 1 1 20%"
          autocomplete="off"
          icon="magnifying-glass"
          @input="onInputChange"
          @keyup="keyPressHandler"
          @keydown.tab="onBlurHandler"
          @blur="onBlurHandler"
        />
      </div>
    </div>
    <div class="max-limit">
      <span
        v-if="getImportSearchIds.length > 0"
        class="max-limit-message app__placeholder"
        data-test="remaining-entries-count"
      >
        <span class="mds-text--small-bold mds-secondary__text-color">
          {{ searchIdLimit - getImportSearchIds.length }}
        </span>
        ({{ labels.remainingEntries }})
      </span>
      <span v-else class="max-limit-message app__placeholder">
        <span class="mds-text--small-bold mds-secondary__text-color"> {{ searchIdLimit }} </span>
        ({{ labels.remainingEntries }})
      </span>
    </div>
  </div>
  <div>
    <div v-if="noResultIds.length > 0">
      <SearchResultErrorView :ids="noResultIds" @close-event="resetError()" />
    </div>

    <div v-if="errorCode">
      <mc-notification
        appearance="error"
        fit="small"
        icon="exclamation-octagon"
        closable
        data-test="error-message-container"
      >
        <span> {{ globalStore.sitecoreData.http_errors[errorCode] }}</span>
      </mc-notification>
    </div>

    <section class="search-actions item-inline action-buttons button-top-margin-15px">
      <mc-button
        data-test="clear-button"
        variant="outlined"
        fit="medium"
        appearance="neutral"
        :label="labels.clear"
        @click="clearSearchIds"
      >
      </mc-button>
      <mc-button
        :disabled="!Object.keys(tempSelectedTerminal).length || getImportSearchIds.length == 0"
        data-test="search-button"
        fit="medium"
        :label="labels.search"
        :loading="commonSearchStore.isScreenLoadingBack || importAssets.isLoading"
        @click="validateIds"
      >
      </mc-button>
    </section>
  </div>
</template>
<style lang="scss" scoped>
.import_availability_heading {
  margin-top: 20px;
  font-size: 16px;
}
</style>
